import { type CalendarSpelling, spelled } from '@app/shared'
import { LETTERHEAD_PRESETS, plain } from '@app/stationery'
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Columns3,
  Download,
  FileText,
  Languages,
  Printer,
  Ruler,
  Stamp,
} from 'lucide-react'
import { civilSpanOf, hebrewYearOf } from '../lib/fall-pattern'
import { printPdf } from '../lib/print'
import {
  Button,
  Combobox,
  Field,
  InfoTip,
  Select,
  Toolbar,
  Tooltip,
} from '../ui'
import { COLUMN_GROUPS, CUSTOM } from './schedule-sheet'

type SheetPreset = { key: string; label: string; description: string }
type SheetColumn = {
  id: string
  role: string | null
  family: string
  onOrdinaryShabbos: boolean
  labelHe: string
  labelEn: string
}
type FallPattern = {
  weekday: string
  flagKeys: string[]
  flags: string[]
  startDates: string[]
}
type Occurrence = {
  startDate: string
  labelHe: string
  labelEn: string
  specialShabbos?: string[] | null
}

export function ScheduleToolbar({
  hebrewYear,
  setHebrewYear,
  yearOptions,
  spelling,
  occasionKey,
  setOccasionKey,
  occasionOptions,
  isYear,
  sheetKey,
  chooseSheet,
  presets,
  isWeekly,
  fallPatterns,
  selectedPattern,
  patternKey,
  nextStart,
  setFrom,
  selectedStart,
  occurrences,
  step,
  sheetReady,
  spaceId,
  sheetUrl,
  showSubtitle,
  setShowSubtitle,
  vocabulary,
  inGroup,
  sheetColumns,
  chooseColumns,
  showCivilDate,
  setShowCivilDate,
  preview,
  setPreview,
  working,
  setWorking,
  showConfidence,
  setShowConfidence,
  letterhead,
  chooseLetterhead,
}: {
  hebrewYear: number
  setHebrewYear: (year: number) => void
  yearOptions: number[]
  spelling: CalendarSpelling
  occasionKey: string
  setOccasionKey: (key: string) => void
  occasionOptions: { value: string; label: string; hint?: string }[]
  isYear: boolean
  sheetKey: string
  chooseSheet: (next: string) => void
  presets: SheetPreset[]
  isWeekly: boolean
  fallPatterns: FallPattern[]
  selectedPattern: FallPattern | undefined
  patternKey: (p: { weekday: string; flagKeys: string[] }) => string
  nextStart: (p: { startDates: string[] }) => string
  setFrom: (date: string) => void
  selectedStart: string | undefined
  occurrences: { data?: Occurrence[] }
  step: (delta: number) => void
  sheetReady: boolean
  spaceId: string | null
  sheetUrl: (space: string) => string
  showSubtitle: boolean
  setShowSubtitle: (value: boolean) => void
  vocabulary: SheetColumn[]
  inGroup: (
    c: { role: string | null; family: string },
    g: (typeof COLUMN_GROUPS)[number],
  ) => boolean
  sheetColumns: string[]
  chooseColumns: (next: string[]) => void
  showCivilDate: boolean
  setShowCivilDate: (value: boolean) => void
  preview: boolean
  setPreview: (value: boolean) => void
  working: boolean
  setWorking: (value: boolean) => void
  showConfidence: boolean
  setShowConfidence: (value: boolean) => void
  letterhead: string
  chooseLetterhead: (next: string) => void
}) {
  return (
    <Toolbar
      fields={
        <>
          <Field label="Year">
            <Combobox
              value={String(hebrewYear)}
              onChange={(v) => setHebrewYear(Number(v))}
              placeholder="Year"
              options={yearOptions.map((y) => ({
                value: String(y),
                label: String(y),
                hint: civilSpanOf(y),
              }))}
            />
          </Field>
          <Field label="Occasion">
            <Combobox
              value={occasionKey}
              onChange={setOccasionKey}
              bilingual
              placeholder="Occasion"
              options={occasionOptions}
            />
          </Field>
          {/* The year sheet's second field, where a chag's would be: which
            columns it carries. A shipped set by name, or the set you built. */}
          {isYear ? (
            <Field label="Sheet">
              <Select
                value={sheetKey}
                onValueChange={chooseSheet}
                options={[
                  ...presets.map((p) => ({
                    value: p.key,
                    label: p.label,
                    description: p.description,
                  })),
                  {
                    value: CUSTOM,
                    label: 'Custom',
                    description: 'The columns you choose.',
                  },
                ]}
              />
            </Field>
          ) : null}
          {/* How the chag falls is what drives its schedule, so let the gabbai
            pick that directly — "Starts Thursday" jumps to the soonest year it
            does, and the resolved sheet shows what follows (eruv tavshilin, the
            extra day). Every way it can fall is offered, including the ones
            decades out: the rare year is exactly the one worth setting up before
            it arrives. Only offered when it can fall more than one way. */}
          {!isWeekly && fallPatterns.length > 1 ? (
            <Field label="Falls on">
              <Combobox
                value={selectedPattern ? patternKey(selectedPattern) : ''}
                onChange={(k) => {
                  const p = fallPatterns.find((x) => patternKey(x) === k)
                  if (p) {
                    setFrom(nextStart(p))
                    setHebrewYear(hebrewYearOf(nextStart(p)))
                  }
                }}
                placeholder="Any"
                options={fallPatterns
                  .slice()
                  .sort((a, b) => nextStart(a).localeCompare(nextStart(b)))
                  .map((p) => {
                    const start = nextStart(p)
                    return {
                      value: patternKey(p),
                      // The flags are what make one Sunday different from
                      // another, so they name the option when there are two.
                      label: `Starts ${p.weekday}${
                        fallPatterns.filter((x) => x.weekday === p.weekday)
                          .length > 1 && p.flags.length
                          ? ` · ${p.flags.join(', ')}`
                          : ''
                      }`,
                      hint: `${hebrewYearOf(start)} · ${new Date(
                        `${start}T12:00:00`,
                      ).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}`,
                    }
                  })}
              />
            </Field>
          ) : null}
          {/* A chag happens once in a given year, so Occasion + Year already
            names it. Only Shabbos needs a third chooser to pick which of ~52. */}
          {isWeekly ? (
            <Field label={spelled('Shabbos', spelling)}>
              <Combobox
                value={selectedStart ?? null}
                onChange={setFrom}
                bilingual
                placeholder="Search by parsha…"
                options={(occurrences.data ?? []).map((o) => ({
                  value: o.startDate,
                  // Searchable in either script: the Hebrew parsha is the label,
                  // the English name and date ride along in the hint.
                  label: plain(o.labelHe),
                  short: new Date(`${o.startDate}T12:00:00`).toLocaleDateString(
                    'en-US',
                    {
                      month: 'short',
                      day: 'numeric',
                    },
                  ),
                  hint: `${o.labelEn}${
                    (o.specialShabbos ?? []).length
                      ? ` · ${plain((o.specialShabbos ?? []).join(', '))}`
                      : ''
                  } · ${new Date(`${o.startDate}T12:00:00`).toLocaleDateString(
                    'en-US',
                    {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    },
                  )}`,
                }))}
              />
            </Field>
          ) : null}
        </>
      }
      actions={
        <>
          {/* The arrows walk the calendar one sheet at a time, which a year
            of them is not — its own field steps it, by year. */}
          {isYear ? null : (
            <>
              <Button
                variant="quiet"
                icon
                aria-label="Previous on the calendar"
                onClick={() => step(-1)}
              >
                <ChevronLeft className="btn__icon" aria-hidden />
              </Button>
              <Button
                variant="quiet"
                icon
                aria-label="Next on the calendar"
                onClick={() => step(1)}
              >
                <ChevronRight className="btn__icon" aria-hidden />
              </Button>
            </>
          )}
          {/* Printing and downloading are the common acts and changing the
            sheet's options is the rare one, so neither sits inside the band the
            rare one opens. Both read the same option state: the defaults until
            somebody changes them, their choices afterwards.

            Print leads, because a luach is made to go on a wall — the file is
            what somebody wants when they are sending it on instead. */}
          <Tooltip label="Print">
            <Button
              variant="secondary"
              icon
              aria-label="Print"
              disabled={!sheetReady}
              onClick={() => {
                if (spaceId) printPdf(sheetUrl(spaceId))
              }}
            >
              <Printer className="btn__icon" aria-hidden />
            </Button>
          </Tooltip>
          <Tooltip label="Download PDF">
            <Button
              variant="secondary"
              icon
              aria-label="Download PDF"
              disabled={!sheetReady}
              onClick={() => {
                if (spaceId)
                  window.open(sheetUrl(spaceId), '_blank', 'noopener')
              }}
            >
              <Download className="btn__icon" aria-hidden />
            </Button>
          </Tooltip>
        </>
      }
      options={
        isYear ? (
          <>
            <InfoTip label="About this sheet">
              <p>
                One row per {spelled('Shabbos', spelling)} of the year. A week
                whose {spelled('Shabbos', spelling)} is Yom Tov or{' '}
                {spelled('Chol Hamoed', spelling)} is named for the chag and
                carries the chag's own times. A column that does not apply to a
                week prints blank.
              </p>
              <p>
                <strong>English headings</strong> sets each column's English
                name under its Hebrew one.
              </p>
              <p>
                <strong>Columns</strong> builds a sheet of your own. Picking one
                makes the sheet Custom, starting from the set you were looking
                at — a shipped set always prints what its name says.
              </p>
              <p>
                It offers what an ordinary {spelled('Shabbos', spelling)} has:
                the times your shul keeps every week, and any zman. A chag's own
                line — Kol Nidrei, Hakafos — is not there, since it would reach
                a {spelled('Shabbos', spelling)} only in a year when that chag
                falls on one.
              </p>
            </InfoTip>
            <Button
              variant="secondary"
              on={showSubtitle}
              onClick={() => setShowSubtitle(!showSubtitle)}
            >
              <Languages className="btn__icon" aria-hidden />
              English headings
            </Button>
            {/* Which columns the sheet carries — a tool, like the letterhead
              beside it on the week's sheet, for the same reason: a label above
              it would stand it taller than the band it sits in. The list is
              grouped by the day a column reads, because a shul's mincha appears
              twice and the erev one is not the Shabbos one. */}
            <Tooltip label="Columns">
              <Select
                variant="tool"
                icon={Columns3}
                multiple
                searchable
                bilingual
                summary={`${sheetColumns.length} columns`}
                values={sheetColumns}
                onValuesChange={chooseColumns}
                options={COLUMN_GROUPS.map((g) => ({
                  label: spelled(g.label, spelling),
                  /* Hebrew leads, English follows — the sheet's own
                         heading is the Hebrew one, so the list reads in the
                         script the column will print in. Either script finds
                         it: the filter reads the hint too. */
                  options: vocabulary
                    .filter((c) => inGroup(c, g) && c.onOrdinaryShabbos)
                    .map((c) => ({
                      value: c.id,
                      label: plain(c.labelHe),
                      hint: c.labelEn,
                    })),
                })).filter((g) => g.options.length > 0)}
              />
            </Tooltip>
          </>
        ) : (
          <>
            {/* One ⓘ for the row, not one per button. Each of these carried its
                explanation in a native `title` — OS-styled, on somebody else's
                delay, and never shown to a keyboard user at all. They are
                sentences, so they are an InfoTip; and a region gets one entry
                point, so four glyphs in a row of four buttons would be the thing
                that rule exists to prevent. */}
            <InfoTip label="About the sheet options">
              <p>
                <strong>Subtitle</strong> prints the English name under each
                Hebrew label. Every line gains a second row, so the sheet sets
                tighter to hold one page.
              </p>
              <p>
                <strong>English date</strong> sets each day heading's date in
                English — the Hebrew date transliterated, with the civil date
                beside it. Off, the Hebrew date is set in Hebrew letters.
              </p>
              <p>
                <strong>Page preview</strong> fetches the printed document — the
                same PDF the printer is handed — and shows that, so what you are
                looking at is the sheet itself rather than a drawing of it.
              </p>
              <p>
                <strong>Letterhead</strong> is four independent choices about
                the masthead: the form it takes, whether its line is single or
                double, whether it is drawn in the sheet's structure gray or its
                one gold accent, and how large the name is set. The footer
                answers the first three, so the page opens and closes on the
                same figure — but never the size, because an address line is
                looked up rather than read across a room.
              </p>
              <p>
                <strong>Logic</strong> shows the rule behind each time — what it
                hangs off, by how much, and what put it on this sheet.
                Downloading with it on gives the reasoning sheet rather than the
                schedule: the one to hand the gabbai, running to as many pages
                as it takes instead of being fitted to one.
              </p>
              <p>
                <strong>Needs review</strong> marks the rules still waiting on
                an answer, with the question to ask about each. On by default,
                because a sheet sent out to be checked is the last place a guess
                should read as settled.
              </p>
            </InfoTip>
            <Button
              variant="secondary"
              on={showSubtitle}
              onClick={() => setShowSubtitle(!showSubtitle)}
            >
              <Languages className="btn__icon" aria-hidden />
              Subtitle
            </Button>
            <Button
              variant="secondary"
              on={showCivilDate}
              onClick={() => setShowCivilDate(!showCivilDate)}
            >
              <CalendarDays className="btn__icon" aria-hidden />
              English date
            </Button>
            <Button
              variant="secondary"
              on={preview}
              onClick={() => setPreview(!preview)}
            >
              <FileText className="btn__icon" aria-hidden />
              Page preview
            </Button>
            <Button
              variant="secondary"
              on={working}
              onClick={() => setWorking(!working)}
            >
              <Ruler className="btn__icon" aria-hidden />
              Logic
            </Button>
            {/* Only meaningful once there is reasoning to qualify, so it
              appears with it rather than sitting inert beside it. */}
            {working ? (
              <Button
                variant="secondary"
                on={showConfidence}
                onClick={() => setShowConfidence(!showConfidence)}
              >
                <CircleAlert className="btn__icon" aria-hidden />
                Needs review
              </Button>
            ) : null}
            {/*
             * The letterhead is the congregation's own, and changing it
             * changes it on every sheet the shul prints.
             *
             * ONE choice, not four. Where the mark sits, what it is, which ink
             * and how loud were four controls, which is seventy-two
             * combinations to get lost in with no way to tell a good one from a
             * bad one. These are looks somebody designed.
             */}
            {/*
             * A TOOL, not a labeled field. The band is a strip of controls
             * of one height and one edge, and a field's label sits above its
             * control — which makes it taller than everything beside it and
             * drops its control below their line. The icon carries the name
             * the label was carrying, and the tooltip says it in words.
             */}
            <Tooltip label="Letterhead">
              <Select
                variant="tool"
                icon={Stamp}
                value={letterhead}
                onValueChange={chooseLetterhead}
                options={LETTERHEAD_PRESETS.map((preset) => ({
                  value: preset.key,
                  label: preset.label,
                  // Each look says what it is: a list of six names is six
                  // things to try one at a time to find out.
                  description: preset.description,
                }))}
              />
            </Tooltip>
          </>
        )
      }
    />
  )
}
